import React, { useState, useEffect } from 'react'
import StoryblokClient from 'storyblok-js-client'
import Layout from '../modules/Layout'
import BodyBlocks from '../technical/BodyBlocks'
import HeroBlock from '../blocks/HeroBlock'
import ProductIntroBlock from '../blocks/ProductIntroBlock'
import PconUiToolBlock from '../blocks/PconUiToolBlock'
import DownloadsBlock from '../blocks/DownloadsBlock'
import RelatedProductsBlock from '../blocks/RelatedProductsBlock'

import { useTranslation } from 'react-i18next'
import SEO from '../modules/Seo'
import InpageNavBlock from '../blocks/InpageNavBlock'
import SocialShareBlock from '../blocks/SocialShareBlock'
import FaqBlock from '../blocks/FaqBlock'
import EnvironmentBlock from '../blocks/EnvironmentBlock'

const ProductPage = ({ content, locale, name, alternates }) => {
  const [similarProducts, setSimilarProducts] = useState([])
  const [filteredPublicUploads, setFilteredPublicUploads] = useState([])
  const { t } = useTranslation()
  const Storyblok = new StoryblokClient({
    accessToken: process.env.GATSBY_STORYBLOK_ACCESS_TOKEN,
    cache: {
      clear: 'auto',
      type: 'memory',
    },
  })

  const getRelatedProducts = async () => {
    let relatedProducts = []
    await Promise.all(
      content.similar_products.map(async (product) => {
        const response = await Storyblok.get(`cdn/stories/${product}`, {
          version: 'published',
        })
        relatedProducts.push(response.data.story)
      })
    ).then(() => {
      setSimilarProducts(relatedProducts)
    })
  }

  const validPublicUploads = (content) => {
    const invalidIds = [6, 8, 9, 34, 48, 62]
    return content?.filter((item) => !invalidIds.includes(item.id))
  }

  useEffect(() => {
    getRelatedProducts()
    setFilteredPublicUploads(validPublicUploads(content.public_uploads))
    return () => {
      setSimilarProducts([])
    }
  }, [])

  var inpageNavItems = []
  let hasProductGallery = false
  let hasImageSlider = false
  let hasStaticDownloads = false

  if (content && content.body) {
    inpageNavItems.push({
      title: t('product_page.overview.nav_title'),
      url: '#top',
    })

    content.body.forEach((block) => {
      if (block.component == 'product_gallery_block' && !hasProductGallery) {
        inpageNavItems.push({
          title: t('product_page.product_gallery_block.nav_title'),
          url: '#' + block._uid,
        })
        hasProductGallery = true
      }
      if (block.component == 'image_slider_block' && !hasImageSlider) {
        inpageNavItems.push({
          title: t('product_page.image_slider_block.nav_title'),
          url: '#' + block._uid,
        })
        hasImageSlider = true
      }
      if (block.component == 'static_downloads_block' && !hasStaticDownloads) {
        inpageNavItems.push({
          title: t('product_page.downloads_block.nav_title'),
          url: '#' + block._uid,
        })
        hasStaticDownloads = true
      }
    })

    if (content.pcon_iframe_url) {
      inpageNavItems.push({
        title: t('product_page.pcon_ui_tool_block.nav_title'),
        url: '#pcon',
      })
    }
    if (content?.faqs?.length > 0) {
      inpageNavItems.push({
        title: t('product_page.faq_block.nav_title'),
        url: '#faq',
      })
    }
    if (content.public_uploads?.length > 0) {
      inpageNavItems.push({
        title: t('product_page.downloads_block.nav_title'),
        url: '#downloads',
      })
    }
  }

  // Prepare fake Breadcrumbs

  let categorySlug = ''

  if (typeof window !== 'undefined') {
    let pathnameSplit = window.location?.pathname?.endsWith('/')
      ? window.location?.pathname?.slice(0, -1).split('/')
      : window.location?.pathname?.split('/')

    let category = pathnameSplit[pathnameSplit?.length - 2]

    if (category === 'softseating') {
      category = 'soft-seating'
    }

    categorySlug = t('product_page.products_slug') + category + '/'
  }

  let fakeBreadcrumbs = [
    {
      title: t('product_page.products'),
      slug: t('product_page.products_slug'),
    },
    {
      title: content?.main_category?.title,
      slug: categorySlug,
    },
  ]
  return (
    <div>
      <Layout locale={locale} alternates={alternates}>
        <SEO
          content={content}
          name={name}
          /*  title={`${name} - ${content?.headline}`} */
          og_locale={locale}
        />
        <span id="top"></span>
        <HeroBlock
          block={{
            hero_mirrored: content.hero_mirrored,
            headline: name,
            image: content.main_image,
            hero_variant_class: 'kn-product-hero',
            video_url: content.video_url,
          }}
        />
        <InpageNavBlock
          block={{
            breadcrumbs: fakeBreadcrumbs,
            inpage_nav_items: inpageNavItems,
          }}
        />
        <SocialShareBlock />
        <ProductIntroBlock
          block={{
            product_image_hidden: content.product_image_hidden,
            image: content.isolated_image,
            alt: content.isolated_image_alt,
            name: name,
            headline: content.headline,
            intro: content.intro,
            highlight_text: content.highlight_text,
            main_text: content.main_text,
            main_award: content.uploads?.main_award,
            awards: content?.design_awards,
            hero_mirrored: content.hero_mirrored,
          }}
        />
        <BodyBlocks body={content.body} />

        {content.pcon_iframe_url?.length > 0 && (
          <>
            <span id="pcon"></span>
            <PconUiToolBlock
              block={{
                headline: t('product_page.pcon_ui_tool_block.title'),
                description: t('product_page.pcon_ui_tool_block.description'),
                pcon_ui_tool_url: content.pcon_iframe_url,
                custom_pcon_ui_tool_url: content.custom_pcon_iframe_url,
              }}
            />
          </>
        )}
        {content.faqs?.length > 0 && (
          <>
            <span id="faq"></span>
            <FaqBlock
              block={{ headline: 'Service und FAQ', faq_items: content.faqs }}
            />
          </>
        )}
        {content.environment_awards?.length > 0 && (
          <EnvironmentBlock block={{ awards: content.environment_awards }} />
        )}
        {content.public_uploads?.length > 0 && (
          <>
            <span id="downloads"></span>
            <DownloadsBlock
              block={{
                headline: t('product_page.downloads_block.nav_title'),
                download_types: filteredPublicUploads,
                categorySlug: content?.uploads.mdb_category_slug,
              }}
            />
          </>
        )}
        {content.similar_product_relations?.length > 0 && (
          <RelatedProductsBlock
            block={{
              headline: t('product_page.related_products_block.title'),
              subheadline: t('product_page.related_products_block.subtitle'),
              products: similarProducts,
            }}
          />
        )}
      </Layout>
    </div>
  )
}

export default ProductPage
